import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import {
  browserStorageKey,
  BrowserStorageResourceType,
} from '../resources/BrowserStorage';
import { browserStorageSafe } from './browserStorageSafe';

// This hook accesses browserStorageSafe directly (instead of via BrowserStorageResource)
// because it needs synchronous reads and runs in _app.tsx before the resource
// provider is initialized.
function getStoredRoute(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    let stored: BrowserStorageResourceType = JSON.parse(
      browserStorageSafe.getItem(browserStorageKey) || '{}',
    );
    return stored?.lastRoute;
  } catch {
    return undefined;
  }
}

function setStoredRoute(route: string | undefined) {
  if (typeof window === 'undefined') return;
  try {
    let stored: BrowserStorageResourceType = JSON.parse(
      browserStorageSafe.getItem(browserStorageKey) || '{}',
    );
    if (route) {
      stored = { ...stored, lastRoute: route };
    } else {
      delete stored?.lastRoute;
    }
    browserStorageSafe.setItem(browserStorageKey, JSON.stringify(stored));
  } catch { }
}

function syncStoredRoute(path: string) {
  if (path === '/') {
    setStoredRoute(undefined);
  } else {
    setStoredRoute(path);
  }
}

export function useRouteMemory() {
  let router = useRouter();
  let didInitialRouteCheck = useRef(false);

  useEffect(() => {
    if (didInitialRouteCheck.current) return;
    didInitialRouteCheck.current = true;

    let storedRoute = getStoredRoute();
    if (storedRoute && storedRoute !== '/' && router.asPath === '/') {
      router.replace(storedRoute);
    }
  }, [router]);

  useEffect(() => {
    let handleRouteChange = (url: string) => syncStoredRoute(url);

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    let handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        syncStoredRoute(window.location.pathname);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
